import { ApiHttpError } from '@/types';
import {
  mapPointsInvoiceDto,
  mapPointsOrderDto,
  mapPointsPackageDto,
  mapPointsRefundDto,
  refundAmountByFormula,
  type PointsOrderDto,
  type PointsPackageDto,
  type PointsRefundDto,
} from '@/types/points';

import type { PointsService } from './pointsService';

/**
 * fixturePointsService — 點數域的 **demo 實作**(比照 `fixtureCheckService`)
 * -----------------------------------------------------------------------------
 * ⚠️ **不是任何環境的預設**:真實路徑一律 `httpPointsService`。只在兩個地方活著 ——
 * 單元測試,以及 demo flag 明確打開(`localStorage['hai.pointsDemo']` = `1` / `atm` + reload)時,
 * 讓 BE 還沒部署前也能在瀏覽器把**購買 / 回跳 / 退款**三條流程走一遍(UI 審查)。
 * 回應形狀照 Creative BE docs/141 v2.1 的 DTO,經同一支 mapper —— demo 畫面與真 BE 走同一條轉換。
 *
 * 模擬的 Mentor 行為(計劃書 §7 v1.0.1):
 *   - 「前往付款」的 `pay_url` 直接就是**付款完成後的回跳網址**(跳過 Mentor 付款頁本身 —— 那頁是
 *     Mentor 後端渲染的,不歸 Creative FE)。Mentor 會保留 return_url 原 query 再疊 `status` /
 *     `display_id`:`1` = 刷卡成功 `status=S`;`atm` = 取號返回 `status=`(空字串 = 結果未定)。
 *   - 刷卡單回跳後要 **兩次** `getOrder(ref, {refresh:true})` 才入點 —— 模擬「推播還沒到、靠輪詢保底」。
 *   - 退款手續費 NT$30(含稅;Mentor 財務後台值,demo 隨便定),申請後經過兩次讀取才結案。
 *
 * 狀態存 `sessionStorage`:付款是**整頁導走再導回來**,記憶體 state 撐不過那一趟。
 * 同一個分頁內有效,關分頁即消失;登出時隨 user-scoped 鑰匙一起清(clientStorage 登記)。
 */

const STATE_KEY = 'hai.pointsDemoState';
const LATENCY_MS = 280;
const DEMO_REFUND_FEE = 30;

const PACKAGES: PointsPackageDto[] = [
  { code: 'A', paid_amount: 6000, display_amount: 6300, points: 1200, paid_points: 1200, bonus_points: 0 },
  { code: 'B', paid_amount: 12000, display_amount: 12600, points: 2520, paid_points: 2400, bonus_points: 120 },
  { code: 'C', paid_amount: 36000, display_amount: 37800, points: 7920, paid_points: 7200, bonus_points: 720 },
];

interface DemoRefund extends PointsRefundDto {
  ticks: number;
}

interface DemoOrder {
  ref: string;
  code: string;
  /** display_status。 */
  status: string;
  createdAt: string;
  paidAt: string | null;
  used: number;
  payment: 'CARD' | 'ATM' | null;
  business: string | null;
  refreshTicks: number;
  refund: DemoRefund | null;
}

interface DemoState {
  v: 2;
  orders: DemoOrder[];
  seq: number;
}

function pkgOf(code: string): PointsPackageDto {
  const p = PACKAGES.find((x) => x.code === code);
  if (!p) throw new ApiHttpError(422, { code: 'MENTOR_PACKAGE_NOT_FOUND', message: `unknown package ${code}` });
  return p;
}

function isoDaysAgo(days: number, hh: number, mm: number): string {
  const d = new Date(Date.now() - days * 86_400_000);
  d.setHours(hh, mm, 0, 0);
  return d.toISOString();
}

function seedState(): DemoState {
  const base = { refreshTicks: 0, refund: null };
  return {
    v: 2,
    seq: 0,
    orders: [
      {
        ...base,
        ref: 'democ0000000001',
        code: 'C',
        status: 'PENDING_PAYMENT',
        createdAt: isoDaysAgo(3, 10, 12),
        paidAt: null,
        used: 0,
        payment: 'ATM',
        business: 'Demo Coffee Studio',
      },
      {
        ...base,
        ref: 'demob0000000001',
        code: 'B',
        status: 'PAID',
        createdAt: isoDaysAgo(5, 14, 5),
        paidAt: isoDaysAgo(5, 14, 7),
        used: 540,
        payment: 'CARD',
        business: 'Demo Marketing Co., Ltd.',
      },
      {
        ...base,
        ref: 'demoa0000000001',
        code: 'A',
        status: 'PAID',
        createdAt: isoDaysAgo(20, 9, 31),
        paidAt: isoDaysAgo(19, 10, 2),
        used: 1200,
        payment: 'ATM',
        business: 'Demo Marketing Co., Ltd.',
      },
    ],
  };
}

function load(): DemoState {
  try {
    const raw = sessionStorage.getItem(STATE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as DemoState;
      if (parsed && parsed.v === 2 && Array.isArray(parsed.orders)) return parsed;
    }
  } catch {
    // 隱私模式 / 壞資料 → 重新 seed
  }
  return seedState();
}

function save(s: DemoState): void {
  try {
    sessionStorage.setItem(STATE_KEY, JSON.stringify(s));
  } catch {
    // 存不進去只影響 demo 跨頁延續,不影響當頁
  }
}

function delay(): Promise<void> {
  return new Promise((r) => setTimeout(r, LATENCY_MS));
}

function notFound(): ApiHttpError {
  return new ApiHttpError(404, { code: 'MENTOR_ORDER_NOT_FOUND', message: 'order not found' });
}

function credited(o: DemoOrder): boolean {
  return o.status === 'PAID' || o.status === 'REFUNDING' || o.status === 'REFUNDED';
}

/** 本單還剩幾點(含退款圈存中);已退款 = 0。 */
function remainingOf(o: DemoOrder): number {
  if (!credited(o) || o.status === 'REFUNDED') return 0;
  return Math.max(0, pkgOf(o.code).points - o.used);
}

function refundDto(r: DemoRefund, ref: string): PointsRefundDto {
  return {
    refund_id: r.refund_id,
    order_ref: ref,
    status: r.status,
    points: r.points,
    amount: r.amount,
    fee: r.fee,
    refunded_amount: r.refunded_amount,
    failure_code: r.failure_code,
    created_at: r.created_at,
  };
}

function toDto(o: DemoOrder): PointsOrderDto {
  const p = pkgOf(o.code);
  const isCredited = credited(o);
  const remaining = remainingOf(o);
  const held = o.status === 'REFUNDING' ? remaining : 0;
  const activeOrSettled = o.refund !== null && o.refund.status !== 'FAILED';
  return {
    order_ref: o.ref,
    package_code: o.code,
    display_status: o.status,
    can_refund: o.status === 'PAID' && remaining > 0 && !activeOrSettled,
    can_continue_payment: o.status === 'PENDING_PAYMENT',
    quoted_paid_amount: p.paid_amount,
    quoted_display_amount: p.display_amount,
    quoted_points: p.points,
    paid_amount: isCredited ? p.paid_amount : null,
    paid_amount_tax_incl: isCredited ? p.display_amount : null,
    bonus_points: isCredited ? p.bonus_points : null,
    points_total: isCredited ? p.points : null,
    points_remaining: isCredited ? remaining : null,
    points_held_for_refund: held,
    points_used: isCredited ? o.used : null,
    refund: o.refund ? refundDto(o.refund, o.ref) : null,
    payment_method: o.payment,
    business_name: o.business,
    credited_at: o.paidAt,
    created_at: o.createdAt,
  };
}

/** 刷卡單:回跳後第二次 refresh 才「收到推播」入點。 */
function advanceOnRefresh(o: DemoOrder): void {
  if (o.status === 'PENDING_PAYMENT' && o.payment === 'CARD') {
    o.refreshTicks += 1;
    if (o.refreshTicks >= 2) {
      o.status = 'PAID';
      o.paidAt = new Date().toISOString();
    }
  }
}

/** 退款處理中:讀過兩次後結案(模擬折讓 + 退刷的處理期)。 */
function advanceRefund(o: DemoOrder): void {
  const r = o.refund;
  if (o.status !== 'REFUNDING' || !r || r.status === 'SETTLED' || r.status === 'FAILED') return;
  r.ticks += 1;
  r.status = r.ticks >= 2 ? 'SETTLED' : 'SUBMITTED';
  if (r.status === 'SETTLED') {
    r.refunded_amount = Math.max(0, (r.amount ?? 0) - (r.fee ?? 0));
    o.status = 'REFUNDED';
  }
}

/** 模擬 Mentor 疊回跳參數(保留 return_url 原 query,`order_ref` 由 FE 自己放)。 */
function mentorReturn(returnUrl: string, ref: string, mode: 'card' | 'atm'): string {
  const u = new URL(returnUrl, window.location.origin);
  u.searchParams.set('status', mode === 'card' ? 'S' : '');
  u.searchParams.set('display_id', `MT${ref.slice(-8).toUpperCase()}`);
  return u.toString();
}

export function createFixturePointsService(mode: 'card' | 'atm' = 'card'): PointsService {
  return {
    async listPackages() {
      await delay();
      return PACKAGES.map(mapPointsPackageDto);
    },

    async getStatus() {
      await delay();
      return { configured: true };
    },

    async createOrder(req) {
      await delay();
      const s = load();
      const existing = s.orders.find((o) => o.ref === req.orderRef);
      if (existing) {
        if (existing.code !== req.packageCode) {
          throw new ApiHttpError(409, { code: 'MENTOR_ORDER_CONFLICT', message: 'order_ref reused with other params' });
        }
        return { ...mapPointsOrderDto(toDto(existing)), payUrl: mentorReturn(req.returnUrl, existing.ref, mode) };
      }
      pkgOf(req.packageCode);
      const o: DemoOrder = {
        ref: req.orderRef,
        code: req.packageCode,
        status: 'PENDING_PAYMENT',
        createdAt: new Date().toISOString(),
        paidAt: null,
        used: 0,
        // 付款方式是在 Mentor 付款頁選的;demo 直接以模式代表「使用者在那頁選了什麼」。
        payment: mode === 'card' ? 'CARD' : 'ATM',
        business: 'Demo Coffee Studio',
        refreshTicks: 0,
        refund: null,
      };
      s.orders.unshift(o);
      save(s);
      return { ...mapPointsOrderDto(toDto(o)), payUrl: mentorReturn(req.returnUrl, o.ref, mode) };
    },

    async listOrders() {
      await delay();
      const s = load();
      for (const o of s.orders) advanceRefund(o);
      save(s);
      const items = [...s.orders]
        .sort((x, y) => (x.createdAt < y.createdAt ? 1 : -1))
        .map((o) => mapPointsOrderDto(toDto(o)));
      return {
        items,
        nextCursor: null,
        summary: {
          pointsRemaining: items.reduce((n, o) => n + o.pointsRemaining, 0),
          pointsRefunding: items.reduce((n, o) => n + o.pointsRefunding, 0),
        },
      };
    },

    async getOrder(orderRef, opts) {
      await delay();
      const s = load();
      const o = s.orders.find((x) => x.ref === orderRef);
      if (!o) throw notFound();
      if (opts?.refresh) advanceOnRefresh(o);
      advanceRefund(o);
      save(s);
      return mapPointsOrderDto(toDto(o));
    },

    async getPayUrl(orderRef) {
      await delay();
      const o = load().orders.find((x) => x.ref === orderRef);
      if (!o) throw notFound();
      if (o.status !== 'PENDING_PAYMENT') {
        throw new ApiHttpError(409, { code: 'MENTOR_ORDER_NOT_PAYABLE', message: 'order is not pending payment' });
      }
      const ret = `${window.location.origin}/points/return?order_ref=${encodeURIComponent(o.ref)}`;
      return mentorReturn(ret, o.ref, o.payment === 'ATM' ? 'atm' : 'card');
    },

    async getInvoice(orderRef) {
      await delay();
      const o = load().orders.find((x) => x.ref === orderRef);
      if (!o) throw notFound();
      if (!credited(o)) {
        throw new ApiHttpError(404, {
          code: 'MENTOR_REJECTED',
          message: 'invoice not issued yet',
          details: { mentor_code: 'INVOICE_NOT_READY' },
        });
      }
      // 財政部電子發票整合服務平台(公開頁;demo 只示意「資訊 + 新分頁連結」)。
      return mapPointsInvoiceDto({
        url: 'https://www.einvoice.nat.gov.tw/',
        invoice_number: 'AB12345678',
        invoice_date: (o.paidAt ?? o.createdAt).slice(0, 10).replace(/-/g, '/'),
        invoice_time: '14:07:00',
        random_number: '4821',
        // eslint-disable-next-line hai/no-hardcoded-cjk -- demo 資料:鯨躍 invoice_status 的 wire 原值
        invoice_status: '開立',
      });
    },

    async getRefundQuote(orderRef) {
      await delay();
      const o = load().orders.find((x) => x.ref === orderRef);
      if (!o) throw notFound();
      if (!toDto(o).can_refund) {
        throw new ApiHttpError(409, { code: 'MENTOR_REFUND_UNAVAILABLE', message: 'order is not refundable' });
      }
      const p = pkgOf(o.code);
      const remaining = remainingOf(o);
      const amount = refundAmountByFormula(remaining, p.display_amount, p.points);
      return {
        pointsRemaining: remaining,
        pointsTotal: p.points,
        priceInclTax: p.display_amount,
        amount,
        fee: DEMO_REFUND_FEE,
        netAmount: Math.max(0, amount - DEMO_REFUND_FEE),
        refundMethod: o.payment === 'ATM' ? 'BANK' : 'CARD',
      };
    },

    async requestRefund(orderRef, req) {
      await delay();
      const s = load();
      const o = s.orders.find((x) => x.ref === orderRef);
      if (!o) throw notFound();
      if (o.refund && o.refund.refund_id === req.refundId) {
        return mapPointsRefundDto(refundDto(o.refund, o.ref)); // 同鍵重送 = 同一筆
      }
      if (o.refund && o.refund.status === 'SETTLED') {
        throw new ApiHttpError(409, { code: 'MENTOR_REFUND_ALREADY_SETTLED', message: 'already refunded' });
      }
      if (o.refund && o.refund.status !== 'FAILED') {
        throw new ApiHttpError(409, { code: 'MENTOR_REFUND_ALREADY_ACTIVE', message: 'refund in progress' });
      }
      const p = pkgOf(o.code);
      const remaining = remainingOf(o);
      const amount = refundAmountByFormula(remaining, p.display_amount, p.points);
      if (amount !== req.quotedAmount) {
        throw new ApiHttpError(409, { code: 'MENTOR_REFUND_QUOTE_STALE', message: 'quote changed' });
      }
      o.status = 'REFUNDING';
      o.refund = {
        refund_id: req.refundId,
        status: 'HOLDING',
        points: remaining,
        amount,
        fee: DEMO_REFUND_FEE,
        refunded_amount: null,
        failure_code: null,
        created_at: new Date().toISOString(),
        ticks: 0,
      };
      save(s);
      return mapPointsRefundDto(refundDto(o.refund, o.ref));
    },

    async getRefund(refundId) {
      await delay();
      const s = load();
      const o = s.orders.find((x) => x.refund?.refund_id === refundId);
      if (!o || !o.refund) throw new ApiHttpError(404, { code: 'MENTOR_REFUND_NOT_FOUND', message: 'refund not found' });
      advanceRefund(o);
      save(s);
      return mapPointsRefundDto(refundDto(o.refund, o.ref));
    },
  };
}

/** 測試用:清掉 demo 狀態(下一次讀取重新 seed)。 */
export function resetPointsDemoStateForTest(): void {
  try {
    sessionStorage.removeItem(STATE_KEY);
  } catch {
    // ignore
  }
}
